import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Switch } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { useForm } from 'react-hook-form';
import { FormSheet } from '../FormSheet.tsx';
import {
  accountBodyOf,
  accountFormOf,
  carrierAccountFormSchema,
  type CarrierAccountFormValues,
} from './carrier-account-form.ts';
import {
  accountOf,
  CARRIER_SECRET_MAX_LENGTH,
  CARRIER_SETTING_MAX_LENGTH,
  type Carrier,
  type CarrierAccountBody,
} from './carriers.ts';

export type CarrierAccountSheetProps = {
  carrier: Carrier;
  currency: string;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (body: CarrierAccountBody) => void;
};

export const CarrierAccountSheet = ({
  carrier,
  currency,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: CarrierAccountSheetProps) => {
  const { t } = useTranslation();
  const existing = accountOf(carrier);
  const form = useForm<CarrierAccountFormValues>({
    resolver: zodResolver(carrierAccountFormSchema(carrier)),
    defaultValues: accountFormOf(carrier, existing),
    mode: 'onBlur',
  });
  const { errors } = form.formState;

  return (
    <FormSheet
      open
      formId="carrier-account-form"
      title={t('carriers.accountTitle', { carrier: carrier.name })}
      submitting={submitting}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSubmit(accountBodyOf(values, currency));
        })(event);
      }}
    >
      <p>{existing === undefined ? t('carriers.accountHelpNew') : t('carriers.accountHelpReplace')}</p>
      {carrier.credentialFields.map((field) => (
        <Input
          key={field}
          label={t(`carriers.field_${field}`)}
          required
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          maxLength={CARRIER_SECRET_MAX_LENGTH}
          errorText={errors.credentials?.[field] ? t('carriers.fieldRequired') : undefined}
          {...form.register(`credentials.${field}`)}
        />
      ))}
      {carrier.settingFields.map((field) => (
        <Input
          key={field}
          label={t(`carriers.field_${field}`)}
          required
          autoComplete="off"
          maxLength={CARRIER_SETTING_MAX_LENGTH}
          errorText={errors.settings?.[field] ? t('carriers.fieldRequired') : undefined}
          {...form.register(`settings.${field}`)}
        />
      ))}
      <Input
        label={t('carriers.customerFee')}
        helperText={t('carriers.customerFeeHelp')}
        inputMode="decimal"
        suffix={currency}
        autoComplete="off"
        errorText={errors.customerFee ? t('carriers.customerFeeInvalid') : undefined}
        {...form.register('customerFee')}
      />
      <Switch
        label={t('carriers.enabled')}
        helperText={t('carriers.enabledHelp')}
        {...form.register('enabled')}
      />
    </FormSheet>
  );
};
